import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PlusIcon, TrashIcon } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { fetchJobTasks, type JobTask } from '../../api/jobWorkflow'
import { normalizeUnitNumber } from '../../api/turnoverContracts'
import { JobTaskChecklist } from './JobTaskChecklist'

interface UnitAllocation {
  id: string
  job_id: string
  job_line_item_id: string
  property_id: string
  unit_number_snapshot: string
  tier_description_snapshot: string
  completion_status: 'planned' | 'completed' | 'carry_forward' | 'skipped'
}

interface JobLine {
  id: string
  description: string
  contract_line_item_id?: string | null
}

interface Props {
  jobId: string
  serviceType?: string | null
  editable?: boolean
  onPresenceChange?: (hasApartments: boolean) => void
}

export function ApartmentJobWork({
  jobId,
  serviceType,
  editable = false,
  onPresenceChange,
}: Props) {
  const [units, setUnits] = useState<UnitAllocation[]>([])
  const [tasks, setTasks] = useState<JobTask[]>([])
  const [lines, setLines] = useState<JobLine[]>([])
  const [propertyId, setPropertyId] = useState('')
  const [loading, setLoading] = useState(true)
  const [newUnitNumber, setNewUnitNumber] = useState('')
  const [newLineId, setNewLineId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const [unitResult, taskRows, lineResult, jobResult] = await Promise.all([
      supabase
        .from('job_line_item_units')
        .select(
          'id, job_id, job_line_item_id, property_id, unit_number_snapshot, tier_description_snapshot, completion_status'
        )
        .eq('job_id', jobId)
        .order('created_at'),
      fetchJobTasks(jobId),
      supabase
        .from('job_line_items')
        .select('id, description, contract_line_item_id')
        .eq('job_id', jobId)
        .not('contract_line_item_id', 'is', null)
        .order('sort_order'),
      supabase.from('jobs').select('property_id').eq('id', jobId).single(),
    ])
    if (unitResult.error) throw unitResult.error
    if (lineResult.error) throw lineResult.error
    if (jobResult.error) throw jobResult.error
    const nextUnits = unitResult.data || []
    setUnits(nextUnits)
    setTasks(taskRows.filter((task) => !!task.job_line_item_unit_id))
    setLines(lineResult.data || [])
    setPropertyId(jobResult.data?.property_id || '')
    onPresenceChange?.(nextUnits.length > 0)
    setLoading(false)
  }, [jobId, onPresenceChange])

  useEffect(() => {
    load().catch(() => setLoading(false))
  }, [load])

  const tasksByUnit = useMemo(() => {
    const grouped = new Map<string, JobTask[]>()
    for (const task of tasks) {
      const unitId = task.job_line_item_unit_id
      if (!unitId) continue
      grouped.set(unitId, [...(grouped.get(unitId) || []), task])
    }
    return grouped
  }, [tasks])

  const updateUnit = async (unitId: string, values: Partial<UnitAllocation>) => {
    const current = units.find((unit) => unit.id === unitId)
    if (!current) return
    const update: Record<string, unknown> = { ...values, updated_at: new Date().toISOString() }
    if (typeof values.unit_number_snapshot === 'string') {
      update.normalized_unit_number = normalizeUnitNumber(values.unit_number_snapshot)
    }
    if (values.job_line_item_id) {
      update.tier_description_snapshot =
        lines.find((line) => line.id === values.job_line_item_id)?.description || ''
    }
    const { error } = await supabase.from('job_line_item_units').update(update).eq('id', unitId)
    if (error) {
      alert(error.message)
      return
    }
    await load()
  }

  const addApartment = async () => {
    if (!newUnitNumber.trim() || !newLineId || !propertyId) return
    const line = lines.find((item) => item.id === newLineId)
    const { data: inserted, error } = await supabase
      .from('job_line_item_units')
      .insert({
        job_id: jobId,
        job_line_item_id: newLineId,
        property_id: propertyId,
        unit_number_snapshot: newUnitNumber.trim(),
        normalized_unit_number: normalizeUnitNumber(newUnitNumber),
        tier_description_snapshot: line?.description || '',
        completion_status: 'planned',
      })
      .select('id')
      .single()
    if (error) {
      alert(error.message)
      return
    }

    const templateUnit = units.find((unit) => unit.job_line_item_id === newLineId)
    const templateTasks = templateUnit ? tasksByUnit.get(templateUnit.id) || [] : []
    if (templateTasks.length > 0) {
      await supabase.from('job_tasks').insert(
        templateTasks.map((task) => ({
          job_id: jobId,
          job_line_item_unit_id: inserted.id,
          label: task.label,
          sort_order: task.sort_order,
          source: task.source || 'work_spec',
          photo_required: !!task.photo_required,
        }))
      )
    }
    setNewUnitNumber('')
    setNewLineId('')
    await load()
  }

  const removeApartment = async (unit: UnitAllocation) => {
    if (!window.confirm(`Remove apartment ${unit.unit_number_snapshot} from this job?`)) return
    const { error } = await supabase.from('job_line_item_units').delete().eq('id', unit.id)
    if (error) alert(error.message)
    else await load()
  }

  if (loading) return <p className="text-sm text-gray-500">Loading apartment work…</p>
  if (units.length === 0 && !editable) return null

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Apartment checklists</h3>
        <p className="text-sm text-gray-600">
          Progress is tracked independently for every apartment.
        </p>
      </div>
      {units.map((unit) => {
        return (
          <div key={unit.id} className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-[12rem]">
                {editable ? (
                  <input
                    value={unit.unit_number_snapshot}
                    onChange={(event) =>
                      setUnits((current) =>
                        current.map((item) =>
                          item.id === unit.id
                            ? { ...item, unit_number_snapshot: event.target.value }
                            : item
                        )
                      )
                    }
                    onBlur={(event) =>
                      updateUnit(unit.id, { unit_number_snapshot: event.target.value })
                    }
                    className="font-medium px-2 py-1 border border-gray-300 rounded"
                  />
                ) : (
                  <p className="font-medium text-gray-900">
                    Apartment {unit.unit_number_snapshot}
                  </p>
                )}
                {editable ? (
                  <select
                    value={unit.job_line_item_id}
                    onChange={(event) =>
                      updateUnit(unit.id, { job_line_item_id: event.target.value })
                    }
                    className="block mt-1 text-xs border border-gray-300 rounded px-2 py-1"
                  >
                    {lines.map((line) => (
                      <option key={line.id} value={line.id}>
                        {line.description}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-xs text-gray-500">{unit.tier_description_snapshot}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs capitalize rounded-full bg-gray-100 px-2 py-1">
                  {unit.completion_status.replace('_', ' ')}
                </span>
                {editable && (
                  <button
                    type="button"
                    onClick={() => removeApartment(unit)}
                    className="p-1 text-red-600"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <JobTaskChecklist
              jobId={jobId}
              serviceType={serviceType}
              jobLineItemUnitId={unit.id}
              showOneOffBadges={false}
            />
          </div>
        )
      })}
      {editable && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 border border-dashed border-gray-300 rounded-lg p-3">
          <input
            value={newUnitNumber}
            onChange={(event) => setNewUnitNumber(event.target.value)}
            placeholder="Apartment number"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <select
            value={newLineId}
            onChange={(event) => setNewLineId(event.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Select rate tier…</option>
            {lines.map((line) => (
              <option key={line.id} value={line.id}>
                {line.description}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={addApartment}
            disabled={!newUnitNumber.trim() || !newLineId}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>
      )}
    </div>
  )
}
